import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, MessageSquareText, Flame, Gauge, Clock3, HandshakeIcon, Coins } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const FEATURES = [
  { t: "Balasan AI Kontekstual", d: "AI memahami seluruh riwayat percakapan dan membalas natural seperti salesperson terbaik Anda — 24 jam nonstop." },
  { t: "Skor Minat Real-time", d: "Setiap chat diklasifikasikan Hot, Medium, atau Cold secara otomatis, sehingga Anda tahu siapa yang harus di-follow up sekarang." },
  { t: "Penilaian Kualitas Percakapan", d: "Opening, engagement, dan closing dinilai 0-100 lengkap dengan alasan dan saran perbaikan." },
  { t: "Katalog Produk Terinjeksi", d: "AI melihat seluruh katalog dan promo Anda, lalu merekomendasikan produk yang tepat di momen yang tepat." },
  { t: "Human Handoff Mulus", d: "Ambil alih percakapan kapan saja. AI berhenti, Anda lanjut, dan AI bisa kembali saat Anda selesai." },
  { t: "Analytics yang Actionable", d: "Estimasi revenue, jam follow-up terbaik, dan produk terlaris — semua dalam satu dashboard." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [pricing, setPricing] = useState({ price_per_1m: 5000, signup_bonus_tokens: 500000 });
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    api.get("/payments/public-config").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  const cta = () => nav(user ? "/dashboard" : "/register");
  const price = `Rp${pricing.price_per_1m.toLocaleString("id-ID")}`;
  const bonus = pricing.signup_bonus_tokens >= 1_000_000
    ? `${pricing.signup_bonus_tokens / 1_000_000} Juta`
    : `${pricing.signup_bonus_tokens / 1_000} Ribu`;
  const faqs = [
    ["Apakah nomor WhatsApp saya aman?", "Ya. Koneksi dilakukan melalui WAHA (WhatsApp HTTP API) dengan session terisolasi per akun. Anda bisa memutus koneksi kapan saja."],
    ["Bagaimana cara kerja harga per token?", `Anda hanya membayar token yang digunakan AI (${price} per 1 juta token). Tidak ada langganan bulanan dan tidak ada fitur yang dikunci.`],
    ["Apa yang terjadi jika token habis?", "AI berhenti membalas, tapi semua pesan tetap diterima dan tersimpan. Top up kapan saja dan AI langsung aktif kembali."],
    ["Bisakah saya mengambil alih chat dari AI?", "Bisa. Satu klik untuk handoff — AI berhenti membalas dan Anda mengetik manual. Akhiri handoff dan AI melanjutkan."],
    ["Apakah AI bisa dibatasi jam kerjanya?", "Bisa. Atur jadwal respons (misal 09:00-17:00 WIB). Di luar jam itu pesan tetap masuk namun AI tidak membalas."],
  ];

  return (
    <div className="min-h-screen bg-ink text-paper">
      <header className={`fixed top-0 z-50 w-full transition-colors ${scrolled ? "border-b border-white/5 bg-ink/80 backdrop-blur-md" : "bg-transparent"}`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-mint text-ink"><Bot size={17} /></span>
            <span className="font-display text-lg font-semibold">ChatSensei</span>
          </div>
          <nav className="hidden items-center gap-7 text-sm text-muted md:flex">
            <a href="#features" className="transition-colors hover:text-paper">Fitur</a>
            <a href="#pricing" className="transition-colors hover:text-paper">Harga</a>
            <a href="#faq" className="transition-colors hover:text-paper">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="nav-login-btn" onClick={() => nav("/login")} className="text-sm text-muted transition-colors hover:text-paper">Masuk</button>
            <button data-testid="nav-cta-btn" onClick={cta} className="rounded-full bg-paper px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-white">Mulai Gratis</button>
          </div>
        </div>
      </header>

      <section className="hero-glow relative flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="mb-8 flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-md border border-mint/20 bg-mint/[0.05] px-5 py-3" role="img" aria-label="Bot sedang mengetik">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-2 w-2 animate-bounce-dot rounded-full bg-mint" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <h1 className="font-display max-w-[820px] text-5xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
          Sales AI yang membalas WhatsApp Anda. <span className="text-mint">Setiap chat.</span>
        </h1>
        <p className="mt-6 max-w-[520px] text-base leading-relaxed text-muted md:text-lg">
          Hubungkan WhatsApp Bisnis Anda dan biarkan AI menjawab pelanggan, menilai minat, dan menutup penjualan — sementara Anda fokus pada hal lain.
        </p>
        <button data-testid="hero-cta-btn" onClick={cta}
          className="mt-10 w-full max-w-sm rounded-full bg-paper px-6 py-3.5 text-sm font-semibold text-ink transition-transform hover:scale-[1.02]">
          Mulai Gratis — {bonus} Token
        </button>
        <p className="mt-3 font-mono text-xs text-faint">Tanpa kartu kredit · {price} / 1 juta token</p>
      </section>

      <section id="features" className="mx-auto max-w-6xl px-6 py-28">
        <h2 className="font-display max-w-xl text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">
          Apa yang bisa ChatSensei <span className="text-mint">baca</span> dari chat Anda?
        </h2>
        <div className="mt-14 grid gap-x-16 md:grid-cols-2">
          {[FEATURES.slice(0, 3), FEATURES.slice(3)].map((col, ci) => (
            <div key={ci} className="divide-y divide-white/5">
              {col.map((f) => (
                <div key={f.t} className="py-8">
                  <h3 className="text-base font-semibold md:text-lg">{f.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{f.d}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-6xl px-6 py-24">
        <h2 className="font-display max-w-xl text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">
          Bayar berdasarkan <span className="text-mint">token</span> yang Anda gunakan.
        </h2>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {[
            { name: "Trial", price: "Rp0", sub: `${bonus} token gratis saat daftar`, pop: false },
            { name: "Custom", price, sub: "per 1 juta token, top up kapan saja", pop: true },
            { name: "Enterprise", price: "Hubungi kami", sub: "token budget & volume khusus", pop: false },
          ].map((p) => (
            <div key={p.name} className={`relative rounded-2xl border p-7 ${p.pop ? "border-mint/40 bg-mint/5" : "border-white/5 bg-panel"}`}>
              {p.pop && <span className="absolute -top-3 left-6 rounded-full bg-mint px-3 py-1 text-[11px] font-bold text-ink">Paling Populer</span>}
              <h3 className="text-sm font-semibold text-muted">{p.name}</h3>
              <p className="mt-3 font-mono text-3xl font-semibold">{p.price}</p>
              <p className="mt-2 text-sm text-muted">{p.sub}</p>
              <button data-testid={`pricing-cta-${p.name.toLowerCase()}`} onClick={cta}
                className={`mt-7 w-full rounded-full py-2.5 text-sm font-semibold transition-colors ${p.pop ? "bg-mint text-ink hover:bg-mint/85" : "border border-white/10 text-paper hover:border-white/30"}`}>
                Mulai Sekarang
              </button>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-faint">1 juta token ≈ 3.000-5.000 balasan AI. Token dihitung dari input + output setiap pemrosesan pesan. Tidak ada biaya tersembunyi.</p>
      </section>

      <section id="faq" className="mx-auto max-w-3xl px-6 py-24">
        <h2 className="font-display text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Pertanyaan umum</h2>
        <div className="mt-10 divide-y divide-white/5">
          {faqs.map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="cursor-pointer list-none text-base font-medium transition-colors hover:text-mint md:text-lg">{q}</summary>
              <p className="mt-3 text-sm leading-relaxed text-muted">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24 text-center">
        <h2 className="font-display text-3xl font-semibold tracking-[-0.03em] sm:text-5xl">Mulai melayani pelanggan Anda hari ini.</h2>
        <button data-testid="footer-cta-btn" onClick={cta} className="mt-8 rounded-full bg-mint px-8 py-3.5 text-sm font-bold text-ink transition-transform hover:scale-[1.03]">
          Coba Gratis Sekarang
        </button>
      </section>

      <footer className="border-t border-white/5 px-6 py-14">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 md:flex-row md:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-mint text-ink"><Bot size={17} /></span>
              <span className="font-display text-lg font-semibold">ChatSensei</span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-faint">AI sales assistant untuk WhatsApp Bisnis Anda.</p>
          </div>
          <div className="grid grid-cols-3 gap-10 text-sm">
            {[["Produk", ["Fitur", "Harga", "FAQ"]], ["Perusahaan", ["Tentang", "Blog", "Karier"]], ["Bantuan", ["Dokumentasi", "Kontak", "Status"]]].map(([h, links]) => (
              <div key={h}>
                <p className="font-semibold">{h}</p>
                <ul className="mt-3 space-y-2 text-faint">
                  {links.map((l) => <li key={l} className="cursor-pointer transition-colors hover:text-paper">{l}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <p className="mx-auto mt-12 max-w-6xl font-mono text-xs text-faint">© 2026 ChatSensei. Dibuat untuk bisnis Indonesia.</p>
      </footer>
    </div>
  );
}
